/**
 * YJS Collaboration Service for Real-time Collaborative Editing
 *
 * This service manages YJS documents and awareness for collaborative editing
 * in the job paste screen. It tracks which users are editing which rooms
 * and provides real-time updates about active editors.
 */

import { getOrCreateAnonymousUser } from 'utils/anonymousUserUtils';
import * as Y from 'yjs';

export interface FieldEditInfo {
  fieldName: string;
  fieldType: string;
  fieldValue?: any;
  isEditing: boolean;
  startTime: number;
  jobId: string;
}

export interface EditorInfo {
  userId: string;
  userName: string;
  roomName: string;
  jobId?: string;
  isEditing: boolean;
  lastSeen: number;
  color: string;
  currentField?: FieldEditInfo;
}

export interface FieldValueChangeData {
  roomName: string;
  jobId: string;
  fieldName: string;
  fieldValue: any;
  updatedBy: string;
  timestamp: string;
}

export interface JobReorderData {
  roomName: string;
  jobSerials: Record<string, number>; // jobId -> serial number
  reorderedBy: string;
  timestamp: string;
}

export interface JobReorderConfirmationData {
  roomName: string;
  jobSerials: Record<string, number>; // jobId -> serial number
  timestamp: string;
}

export interface ChangesRevertedData {
  roomName: string;
  revertedBy: string;
  reverts: Record<string, any>; // jobId -> { field: originalValue }
  timestamp: string;
}

export interface AwarenessState {
  editors: Map<string, EditorInfo>;
  totalEditors: number;
}

class YJSCollaborationService {
  private static instance: YJSCollaborationService | null = null;
  private doc: Y.Doc | null = null;
  private ws: WebSocket | null = null;
  private awareness: any = null;
  private currentUser: string = '';
  private isConnected: boolean = false;
  private awarenessHandlers: Set<(state: AwarenessState) => void> = new Set();
  private fieldValueChangeHandlers: Set<(data: FieldValueChangeData) => void> = new Set();
  private jobReorderHandlers: Set<(data: JobReorderData) => void> = new Set();
  private jobReorderConfirmationHandlers: Set<(data: JobReorderConfirmationData) => void> =
    new Set();
  private changesRevertedHandlers: Set<(data: ChangesRevertedData) => void> = new Set();
  private roomEditingMap: Y.Map<Y.Map<any>> | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 3000;

  private constructor() {}

  static getInstance(): YJSCollaborationService {
    if (!YJSCollaborationService.instance) {
      YJSCollaborationService.instance = new YJSCollaborationService();
    }
    return YJSCollaborationService.instance;
  }

  /**
   * Initialize YJS document and WebSocket connection
   */
  initialize(): void {
    if (this.doc) {
      return; // Already initialized
    }

    this.currentUser = getOrCreateAnonymousUser();
    this.doc = new Y.Doc();

    // Set up room editing map
    this.roomEditingMap = this.doc.getMap('roomEditing');

    // Create WebSocket connection
    this.connect();
  }

  /**
   * Get WebSocket URL for YJS collaboration
   */
  private getWebSocketUrl(): string {
    let backendUrl;

    if (import.meta.env.VITE_MODE === 'dev') {
      backendUrl = import.meta.env.VITE_BACKEND_SOCKET_ENDPOINT;
    } else {
      backendUrl =
        (window as any).config?.backendSocketEndpoint || (window as any).config?.backendBaseUrl;
    }

    if (!backendUrl) {
      throw new Error('WebSocket backend URL not configured');
    }

    if (backendUrl?.endsWith('/')) {
      backendUrl = backendUrl.slice(0, -1);
    }

    const wsUrl = backendUrl?.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${wsUrl}/ws/yjs-collaboration/`;
  }

  /**
   * Connect to WebSocket
   */
  private connect(): void {
    try {
      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('Failed to connect to YJS collaboration WebSocket:', error);
    }
  }

  /**
   * Handle WebSocket connection open
   */
  private handleOpen(): void {
    this.isConnected = true;
    this.reconnectAttempts = 0;

    // Send user identification
    this.send({
      type: 'user_identification',
      user_id: this.currentUser,
      user_name: this.currentUser
    });
  }

  /**
   * Handle WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'awareness_update':
          this.handleAwarenessUpdate(data);
          break;
        case 'connection_established':
          break;
        case 'user_identification_confirmed':
          break;
        case 'editing_started':
          break;
        case 'editing_stopped':
          break;
        case 'field_editing_started':
          break;
        case 'field_editing_stopped':
          break;
        case 'field_value_updated':
          break;
        case 'field_value_changed':
          this.handleFieldValueChanged(data);
          break;
        case 'job_reorder_update':
          this.handleJobReorderUpdate(data);
          break;
        case 'job_reorder_confirmed':
          this.handleJobReorderConfirmation(data);
          break;
        case 'changes_reverted_update':
          this.handleChangesReverted(data);
          break;
        case 'error':
          break;
        default:
          console.log('Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket connection close
   */
  private handleClose(): void {
    this.isConnected = false;

    // Attempt to reconnect
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

      setTimeout(() => {
        this.connect();
      }, delay);
    }
  }

  /**
   * Handle WebSocket error
   */
  private handleError(error: Event): void {
    console.error('YJS Collaboration WebSocket error:', error);
    console.error('WebSocket URL attempted:', this.getWebSocketUrl());
  }

  /**
   * Send message to WebSocket
   */
  private send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const message = JSON.stringify(data);
      this.ws.send(message);
    } else {
      console.error('YJS Service: Cannot send message - WebSocket not ready:', {
        ws: !!this.ws,
        readyState: this.ws?.readyState
      });
    }
  }

  /**
   * Handle awareness update from server
   */
  private handleAwarenessUpdate(data: any): void {
    const editors = new Map<string, EditorInfo>();

    if (data.active_editors) {
      data.active_editors.forEach((editor: any) => {
        const editorInfo: EditorInfo = {
          userId: editor.user_id,
          userName: editor.user_name,
          roomName: editor.room_name,
          jobId: editor.job_id,
          isEditing: editor.is_editing,
          lastSeen: new Date(editor.last_seen).getTime(),
          color: editor.color,
          currentField: editor.current_field
            ? {
                fieldName: editor.current_field.field_name,
                fieldType: editor.current_field.field_type,
                fieldValue: editor.current_field.field_value,
                isEditing: editor.current_field.is_editing,
                startTime: editor.current_field.start_time,
                jobId: editor.current_field.job_id
              }
            : undefined
        };
        editors.set(editor.user_name, editorInfo);
      });
    }

    // Add current user if they are editing but not in the server response
    if (this.currentUser && !editors.has(this.currentUser)) {
      const currentUserInfo: EditorInfo = {
        userId: this.currentUser,
        userName: this.currentUser,
        roomName: '',
        jobId: '',
        isEditing: false,
        lastSeen: Date.now(),
        color: this.generateUserColor(this.currentUser)
      };
      editors.set(this.currentUser, currentUserInfo);
    }

    const state: AwarenessState = {
      editors,
      totalEditors: Math.max(data.total_editors || 0, editors.size)
    };

    this.notifyAwarenessHandlers(state);
  }

  /**
   * Set initial user state
   */
  private setUserState(): void {
    // User state is now managed by the server
    // No local state management needed
  }

  /**
   * Generate a consistent color for a user
   */
  private generateUserColor(userId: string): string {
    const colors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
      '#85C1E9'
    ];

    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Start editing a room
   */
  startEditingRoom(roomName: string, jobId?: string): void {
    this.send({
      type: 'start_editing_room',
      room_name: roomName,
      job_id: jobId
    });

    // Update room editing map
    if (this.roomEditingMap) {
      if (!this.roomEditingMap.has(roomName)) {
        this.roomEditingMap.set(roomName, new Y.Map());
      }

      const roomMap = this.roomEditingMap.get(roomName);
      if (roomMap) {
        roomMap.set(this.currentUser, {
          jobId: jobId || '',
          startTime: Date.now(),
          isActive: true
        });
      }
    }
  }

  /**
   * Stop editing a room
   */
  stopEditingRoom(roomName: string): void {
    this.send({
      type: 'stop_editing_room',
      room_name: roomName
    });

    // Update room editing map
    if (this.roomEditingMap) {
      const roomMap = this.roomEditingMap.get(roomName);
      if (roomMap) {
        roomMap.delete(this.currentUser);
      }
    }
  }

  /**
   * Get current awareness state
   */
  getAwarenessState(): AwarenessState {
    // This is now managed by the server and updated via WebSocket messages
    // Return empty state as a fallback
    return {
      editors: new Map(),
      totalEditors: 0
    };
  }

  /**
   * Subscribe to awareness changes
   */
  onAwarenessChange(handler: (state: AwarenessState) => void): () => void {
    this.awarenessHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.awarenessHandlers.delete(handler);
    };
  }

  /**
   * Notify all awareness handlers
   */
  private notifyAwarenessHandlers(state?: AwarenessState): void {
    const currentState = state || this.getAwarenessState();
    this.awarenessHandlers.forEach(handler => {
      try {
        handler(currentState);
      } catch (error) {
        console.error('Error in awareness handler:', error);
      }
    });
  }

  /**
   * Start editing a specific field
   */
  startEditingField(
    roomName: string,
    jobId: string,
    fieldName: string,
    fieldType: string,
    fieldValue?: any
  ): void {
    this.send({
      type: 'start_editing_field',
      room_name: roomName,
      job_id: jobId,
      field_name: fieldName,
      field_type: fieldType,
      field_value: fieldValue
    });
  }

  /**
   * Stop editing a specific field
   */
  stopEditingField(roomName: string, jobId: string, fieldName: string): void {
    this.send({
      type: 'stop_editing_field',
      room_name: roomName,
      job_id: jobId,
      field_name: fieldName
    });
  }

  /**
   * Update field value while editing
   */
  updateFieldValue(roomName: string, jobId: string, fieldName: string, fieldValue: any): void {
    const message = {
      type: 'update_field_value',
      room_name: roomName,
      job_id: jobId,
      field_name: fieldName,
      field_value: fieldValue
    };

    // Console log for debugging WebSocket message being sent

    this.send(message);
  }

  /**
   * Handle field value changes from other users
   */
  private handleFieldValueChanged(data: any): void {
    // Console log for debugging WebSocket message being received

    this.fieldValueChangeHandlers.forEach((handler, index) => {
      try {
        handler({
          roomName: data.room_name,
          jobId: data.job_id,
          fieldName: data.field_name,
          fieldValue: data.field_value,
          updatedBy: data.updated_by,
          timestamp: data.timestamp
        });
      } catch (error) {
        console.error(`YJS Service: Error in field value change handler ${index}:`, error);
      }
    });
  }

  /**
   * Handle job reorder updates from other users
   */
  private handleJobReorderUpdate(data: any): void {
    this.jobReorderHandlers.forEach((handler, index) => {
      try {
        handler({
          roomName: data.room_name,
          jobSerials: data.job_serials,
          reorderedBy: data.reordered_by,
          timestamp: data.timestamp
        });
      } catch (error) {
        console.error(`🔄 YJS Service: Error in job reorder handler ${index}:`, error);
      }
    });
  }

  /**
   * Handle job reorder confirmation for the original user who initiated the reorder
   */
  private handleJobReorderConfirmation(data: any): void {
    this.jobReorderConfirmationHandlers.forEach((handler, index) => {
      try {
        handler({
          roomName: data.room_name,
          jobSerials: data.job_serials,
          timestamp: data.timestamp
        });
      } catch (error) {
        console.error(`✅ YJS Service: Error in job reorder confirmation handler ${index}:`, error);
      }
    });
  }

  /**
   * Handle changes reverted messages from other users
   */
  private handleChangesReverted(data: any): void {
    this.changesRevertedHandlers.forEach((handler, index) => {
      try {
        handler({
          roomName: data.room_name,
          revertedBy: data.reverted_by || data.revertedBy,
          reverts: data.reverts,
          timestamp: data.timestamp
        });
      } catch (error) {
        console.error(`🔄 YJS Service: Error in changes reverted handler ${index}:`, error);
      }
    });
  }

  /**
   * Send job reorder message to WebSocket
   */
  sendJobReorder(roomName: string, jobSerials: Record<string, number>): void {
    this.send({
      type: 'job_reorder',
      room_name: roomName,
      job_serials: jobSerials
    });
  }

  /**
   * Send changes reverted message to WebSocket
   */
  sendChangesReverted(roomName: string, reverts: Record<string, any>): void {
    this.send({
      type: 'changes_reverted',
      room_name: roomName,
      reverts: reverts
    });
  }

  /**
   * Subscribe to field value changes
   */
  onFieldValueChange(handler: (data: FieldValueChangeData) => void): () => void {
    this.fieldValueChangeHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.fieldValueChangeHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to job reorder updates
   */
  onJobReorderUpdate(handler: (data: JobReorderData) => void): () => void {
    this.jobReorderHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.jobReorderHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to job reorder confirmations
   */
  onJobReorderConfirmation(handler: (data: JobReorderConfirmationData) => void): () => void {
    this.jobReorderConfirmationHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.jobReorderConfirmationHandlers.delete(handler);
    };
  }

  /**
   * Subscribe to changes reverted messages
   */
  onChangesReverted(handler: (data: ChangesRevertedData) => void): () => void {
    this.changesRevertedHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.changesRevertedHandlers.delete(handler);
    };
  }

  /**
   * Get editors for a specific room
   */
  getRoomEditors(_roomName: string): EditorInfo[] {
    // This will be updated by the server via WebSocket messages
    return [];
  }

  /**
   * Get all active editors
   */
  getAllEditors(): EditorInfo[] {
    // This will be updated by the server via WebSocket messages
    return [];
  }

  /**
   * Check if user is connected
   */
  isWebSocketConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Cleanup and disconnect
   */
  cleanup(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.doc) {
      this.doc.destroy();
      this.doc = null;
    }

    this.awarenessHandlers.clear();
    this.fieldValueChangeHandlers.clear();
    this.jobReorderHandlers.clear();
    this.jobReorderConfirmationHandlers.clear();
    this.isConnected = false;
  }
}

export const yjsCollaborationService = YJSCollaborationService.getInstance();
